import React, { useEffect, useRef, useState } from 'react';
import { useRepo } from '../hooks/useRepo';
import Navbar from '../components/navbar';
import { TableOfContentsPanel } from '../components/TableOfContents';
import { SidebarNavigationPanel } from '../components/SidebarNavigation';
import { usePagePath } from '../hooks/usePagePath';
import { useBasename } from '../hooks/useBasename';
import { useNavigation } from '../hooks/useNavigation';
import { encodeFileToDataUrl } from '../utils/file-tools';
import { highlightElement } from '../utils/prism-config';
import { generateAnchorId } from '../utils/anchor-utils';
import { buildFoamSvg } from '../utils/foam-icons';
import { updateVirtualLinks } from '../utils/virtual-links';
import ContentArea from '../components/ContentArea';

const parser = new DOMParser();

const HEADING_LINK_ICON_SIZES = {
  1: { pixels: 20, rem: '1.25rem' },
  2: { pixels: 18, rem: '1.125rem' },
  3: { pixels: 16, rem: '1rem' },
  4: { pixels: 14, rem: '0.875rem' },
  5: { pixels: 12, rem: '0.75rem' },
  6: { pixels: 11, rem: '0.6875rem' },
};


const View = ({ existingContent }) => {
  const basename = useBasename();
  const { path, isVirtual } = usePagePath();
  const { repo } = useRepo();
  const { goToNotFound } = useNavigation();
  const [content, setContent] = useState(() =>
    renderInitialContentHtml(existingContent, { basename, isVirtual })
  );
  const [navbarEffectiveTop, setNavbarEffectiveTop] = useState(64);
  const [contentWidth, setContentWidth] = useState(0);
  const hasScrolledToHash = useRef(false);
  const sidebarSemaphoreState = useState(null);

  useEffect(() => {
    const loadContent = async () => {
      const ignoreEdits = !isVirtual;
      
      // Only check and create page if we have a preview path
      if (!await repo.pageExists(path)) {
        goToNotFound(path);
        return;
      }
      
      const loadedContent = await repo.getHtmlBody(path, ignoreEdits);
      const loadedMetadata = await repo.getMetadata(path, ignoreEdits);
      document.title = loadedMetadata.title
      setContent(await renderContentHtml(loadedContent, { basename, isVirtual, repo }));
      
      // Scroll to anchor if there's a hash in the URL
      const hash = window.location.hash;
      if (hash && !hasScrolledToHash.current) {
        hasScrolledToHash.current = true;
        // Use setTimeout to ensure DOM is fully rendered
        setTimeout(() => {
          const element = document.getElementById(hash.substring(1));
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    }
    if (repo) {
      loadContent();
    }
  }, [repo, basename, isVirtual, path, goToNotFound]);

  // Track content container width
  useEffect(() => {
    const updateContentWidth = () => {
      const contentContainer = document.getElementById('content-container');
      if (contentContainer) {
        setContentWidth(contentContainer.getBoundingClientRect().width);
      }
    };
    // Initial measurement
    updateContentWidth();
    // Listen for resize events
    window.addEventListener('resize', updateContentWidth);
    return () => {
      window.removeEventListener('resize', updateContentWidth);
    };
  }, [content]); // Re-measure when content changes

  return (
    <>
      <Navbar 
        activePage={isVirtual ? "Preview" : undefined}
        onNavbarInfoChange={setNavbarEffectiveTop}
      />
      <TableOfContentsPanel
        content={content}
        effectiveTop={navbarEffectiveTop}
        contentWidth={contentWidth}
        semaphoreState={sidebarSemaphoreState}
      />
      <SidebarNavigationPanel
        effectiveTop={navbarEffectiveTop}
        contentWidth={contentWidth}
        semaphoreState={sidebarSemaphoreState}
      />
      <div id="content" className="min-h-70 flex items-center justify-center pt-8">
        <div id="content-container" className="w-full max-w-4xl editor-preview !px-6" style={{ backgroundColor: 'transparent' }}>
          <ContentArea content={content} />
        </div>
      </div>
    </>
  )
};

export default View;


const renderInitialContentHtml = (html, { basename, isVirtual } = {}) => {
  if (!html) {
    return html;
  }

  const parsedContent = decorateContentHtml(html, { basename, isVirtual });
  return parsedContent.body.innerHTML;
}


const renderContentHtml = async (html, { basename, isVirtual, repo } = {}) => {
  if (!html) {
    return html;
  }

  const parsedContent = decorateContentHtml(html, { basename, isVirtual });
  if (isVirtual && repo) {
    await updateVirtualMedia(parsedContent, repo);
  }

  return parsedContent.body.innerHTML;
}

const decorateContentHtml = (html, { basename, isVirtual } = {}) => {
  const parsedContent = parser.parseFromString(html, 'text/html');

  restorePromotedCssElements(parsedContent);
  addHeadingLinks(parsedContent);
  if (isVirtual) {
    updateVirtualLinks(parsedContent, basename);
  }

  highlightElement(parsedContent.body);
  return parsedContent;
}


const restorePromotedCssElements = (parsedContent) => {
  const elements = Array.from(parsedContent.head.querySelectorAll(
    'style, link[rel="stylesheet"], link[rel="preload"][as="style"]'
  ));
  if (elements.length === 0) return;

  const fragment = parsedContent.createDocumentFragment();
  elements.forEach(element => fragment.appendChild(element));
  parsedContent.body.insertBefore(fragment, parsedContent.body.firstChild);
};


const updateVirtualMedia = async (parsedContent, repo) => {
  const media = parsedContent.querySelectorAll('img, video, audio, iframe');
  
  const processMedia = async (element) => {
    const src = element.getAttribute('src');
    if (src?.startsWith('/_files/')) {
      try {
        // Strip /_files/ prefix
        const filePath = src.substring(8);
        
        // Get file content from repo
        const fileContent = await repo.files.cat(filePath);
        
        // Encode file content to data URL
        const dataUrl = encodeFileToDataUrl(fileContent, filePath);
        
        if (dataUrl) {
          element.src = dataUrl;
        } else {
          // Keep original src if encoding fails
          console.warn(`Unknown media extension: ${filePath}, keeping original src`);
        }
      } catch (error) {
        console.warn(`Failed to load media ${src}:`, error);
        // Keep original src if loading fails
      }
    }
  }
  const promises = []
  for (const element of media) {
    promises.push(processMedia(element));
  }
  await Promise.all(promises);
}

const getHeadingLinkIconSize = (headingLevel) =>
  HEADING_LINK_ICON_SIZES[headingLevel] || HEADING_LINK_ICON_SIZES[2];



// Function to add heading links to parsed content
const addHeadingLinks = (parsedContent) => {
  const headings = parsedContent.querySelectorAll('h1, h2, h3, h4, h5, h6');

  headings.forEach((heading) => {
    // Skip if already processed
    if (heading.classList.contains('heading-processed')) return;

    // Generate anchor ID from heading text
    const text = heading.textContent || '';
    const anchorId = generateAnchorId(text);
    const headingLevel = Number.parseInt(heading.tagName.substring(1), 10) || 2;
    const iconSize = getHeadingLinkIconSize(headingLevel);

    // Set the heading's ID
    heading.id = anchorId;

    // Create container div
    const container = parsedContent.createElement('div');
    container.className = 'heading-container align-middle';
    container.style.setProperty('--heading-link-size', iconSize.rem);

    const iconSvg = buildFoamSvg(text || anchorId, iconSize.pixels);

    // Create link icon using foam identicon
    const linkIcon = parsedContent.createElement('a');
    linkIcon.href = `#${anchorId}`;
    linkIcon.className = 'heading-link-icon tooltip tooltip-left';
    linkIcon.innerHTML = `<span class="heading-link-mask mask mask-squircle">${iconSvg}</span>`;
    linkIcon.dataset.tip = 'Link to this section';
    linkIcon.setAttribute('aria-label', 'Link to this section');
    linkIcon.style.setProperty('--heading-link-size', iconSize.rem);

    // Wrap heading in container and add link icon
    heading.parentNode.insertBefore(container, heading);
    container.appendChild(linkIcon);
    container.appendChild(heading);

    // Mark as processed
    heading.classList.add('heading-processed');
  });
};
